import { httpClient } from '@/api/httpClient';
import { downloadAuthenticatedFile } from '@/shared/utils/downloadAuthenticatedFile';
import type {
  Inspection,
  InspectionCreatePayload,
  InspectionFormValues,
  MonitoringStatus,
  OverallStatus,
  PaginatedResponse,
  Well,
  WellEvidenceStepKey,
  WellFilters,
  WellFormValues,
  WellGeolocation
} from '../types/wellMonitoring';

/**
 * Builds the multipart body for one well photo. The photo is a child Evidence
 * pointing at the inspection (`related_entity_id`) and tagged in metadata with the
 * well id and the procedure step it documents, so the report can render the three
 * photos per well in order. Shared by the online upload (wizard) and the offline
 * sync worker.
 */
export const buildWellEvidenceFormData = ({
  station,
  inspectionId,
  well,
  step,
  file,
  capturedAt,
  geolocation
}: {
  station: string | number;
  inspectionId: string;
  well: string;
  step: WellEvidenceStepKey;
  file: Blob;
  capturedAt: string;
  geolocation?: WellGeolocation;
}) => {
  const formData = new FormData();
  formData.append('station', String(station));
  formData.append('related_entity_type', 'MonitoringInspection');
  formData.append('related_entity_id', inspectionId);
  formData.append('file', file, `monitoreo-${well}-${step.toLowerCase()}.jpg`);
  formData.append('offline_created_at', capturedAt);
  if (geolocation) {
    formData.append('geolocation', JSON.stringify(geolocation));
  }
  formData.append('metadata', JSON.stringify({ well_id: well, step, captured_at: capturedAt }));
  return formData;
};

const normalizeList = <T>(data: T[] | PaginatedResponse<T>) =>
  Array.isArray(data) ? data : data.results;

/**
 * Converts a `datetime-local` value (or any parseable date) to an ISO 8601
 * string with explicit timezone offset, which the backend stores verbatim.
 */
export const toBackendDateTime = (value: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toISOString();
};

/** Turns a canvas data-URL signature into a PNG File for multipart upload. */
export const dataUrlToFile = async (dataUrl: string, filename = 'operator-signature.png') => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

/** Builds the final create payload from wizard form values. */
export const buildInspectionPayload = (
  stationId: string | number,
  values: InspectionFormValues
): InspectionCreatePayload => {
  const payload: InspectionCreatePayload = {
    station: Number(stationId),
    inspection_datetime: toBackendDateTime(values.inspection_datetime),
    exceptions: Object.values(values.exceptions)
  };

  if (values.latitude && values.longitude) {
    payload.latitude = values.latitude;
    payload.longitude = values.longitude;
  }

  if (values.device_captured_at) {
    payload.device_captured_at = values.device_captured_at;
  }

  const materialDescription = values.material_description?.trim();
  if (materialDescription) {
    payload.material_description = materialDescription;
  }

  return payload;
};

const buildQuery = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.append(key, String(value));
    }
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

const toWellPayload = (values: WellFormValues) => ({
  station: Number(values.station),
  well_type: values.well_type,
  label: values.label,
  is_active: values.is_active
});

export const wellMonitoringApi = {
  // --- Wells (configuration) ---
  async listWells(filters: WellFilters = {}) {
    const query = buildQuery({
      station: filters.station,
      well_type: filters.well_type,
      is_active: filters.is_active
    });
    const data = await httpClient<Well[] | PaginatedResponse<Well>>(`/api/v1/wells/${query}`);
    return normalizeList(data);
  },
  getWell(wellId: string) {
    return httpClient<Well>(`/api/v1/wells/${wellId}/`);
  },
  createWell(values: WellFormValues) {
    return httpClient<Well>('/api/v1/wells/', {
      method: 'POST',
      body: JSON.stringify(toWellPayload(values))
    });
  },
  updateWell(wellId: string, values: Partial<WellFormValues>) {
    const payload: Record<string, unknown> = {};

    if (values.station !== undefined) payload.station = Number(values.station);
    if (values.well_type !== undefined) payload.well_type = values.well_type;
    if (values.label !== undefined) payload.label = values.label;
    if (values.is_active !== undefined) payload.is_active = values.is_active;

    return httpClient<Well>(`/api/v1/wells/${wellId}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  },
  deactivateWell(wellId: string) {
    return httpClient<Well>(`/api/v1/wells/${wellId}/deactivate/`, { method: 'POST' });
  },

  // --- Inspections (append-only lifecycle) ---
  async listInspections(
    filters: { station?: string | number; overall_status?: OverallStatus } = {}
  ) {
    const query = buildQuery({
      station: filters.station,
      overall_status: filters.overall_status
    });
    const data = await httpClient<Inspection[] | PaginatedResponse<Inspection>>(
      `/api/v1/monitoring/inspections/${query}`
    );
    return normalizeList(data);
  },
  getInspection(inspectionId: string) {
    return httpClient<Inspection>(`/api/v1/monitoring/inspections/${inspectionId}/`);
  },
  createInspection(payload: InspectionCreatePayload) {
    return httpClient<Inspection>('/api/v1/monitoring/inspections/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },
  /** `submit` is multipart: the operator signature image seals the declaration. */
  submitInspection(inspectionId: string, signature: File) {
    const formData = new FormData();
    formData.append('operator_signature', signature);
    return httpClient<Inspection>(`/api/v1/monitoring/inspections/${inspectionId}/submit/`, {
      method: 'POST',
      body: formData
    });
  },
  reviewInspection(inspectionId: string) {
    return httpClient<Inspection>(`/api/v1/monitoring/inspections/${inspectionId}/review/`, {
      method: 'POST'
    });
  },
  completeInspection(inspectionId: string) {
    return httpClient<Inspection>(`/api/v1/monitoring/inspections/${inspectionId}/complete/`, {
      method: 'POST'
    });
  },
  rejectInspection(inspectionId: string) {
    return httpClient<Inspection>(`/api/v1/monitoring/inspections/${inspectionId}/reject/`, {
      method: 'POST'
    });
  },
  reopenInspection(inspectionId: string) {
    return httpClient<Inspection>(`/api/v1/monitoring/inspections/${inspectionId}/reopen/`, {
      method: 'POST'
    });
  },

  // --- Status / due date ---
  getStatus(stationId?: string | number) {
    const query = buildQuery({ station: stationId });
    return httpClient<MonitoringStatus>(`/api/v1/monitoring/status/${query}`);
  },

  /**
   * Downloads the sealed inspection's official PDF. The backend generates the
   * artifact once (on first request) and serves the cached, immutable file
   * afterwards; only COMPLETED inspections have a report (draft → 409).
   */
  downloadInspectionReport(inspection: Pick<Inspection, 'id' | 'folio'>) {
    return downloadAuthenticatedFile(
      `/api/v1/monitoring/inspections/${inspection.id}/report/`,
      `${inspection.folio || `monitoreo-${inspection.id}`}.pdf`
    );
  },

  /** Uploads one well photo as a child Evidence of the inspection. */
  uploadEvidence(formData: FormData) {
    return httpClient<{ id: string }>('/api/v1/evidence/', {
      method: 'POST',
      body: formData
    });
  }
};
